import { Link } from "react-router-dom";
import { useState } from "react";
import { Send, Facebook, Instagram, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !agreed) { toast.error("Please agree to the policy first"); return; }
    setLoading(true);
    try {
      await api.post("/subscribers", { email });
      toast.success("You're on the list.");
      setEmail("");
    } catch { toast.error("Could not subscribe."); } finally { setLoading(false); }
  };

  return (
    <footer className="bg-[var(--pz-ink)] text-zinc-300 border-t border-[var(--pz-line)]">
      {/* Newsletter band */}
      <section className="relative overflow-hidden border-b border-[var(--pz-line)]">
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="pz-watermark text-[18vw] leading-none">newsletter</div>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="inline-grid place-items-center h-12 w-12 rounded-full bg-[var(--pz-red)] text-white mb-6">
            <Send className="h-5 w-5" />
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase leading-tight">Get the best match stories <br /> into your inbox</h2>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl mx-auto" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              data-testid="footer-newsletter-input"
              className="pz-input flex-1 text-center sm:text-left"
            />
            <button disabled={loading} data-testid="footer-newsletter-submit" className="btn-red justify-center disabled:opacity-60">
              {loading ? "..." : <><Send className="h-4 w-4" /> Subscribe</>}
            </button>
          </form>
          <label className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="accent-[var(--pz-red)]" />
            I agree to the <Link to="/about" className="underline">Privacy Policy</Link>.
          </label>
        </div>
      </section>

      {/* Bottom */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div className="leading-none">
              <div className="font-display text-lg uppercase font-bold text-white tracking-wide">{settings.site_name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--pz-mute)] mt-1">Team Club</div>
            </div>
          </div>
          <p className="text-zinc-400 text-sm max-w-md">Sri Lanka's premier indoor cricket arena. Premier League nights, corporate tournaments, private bookings and a fanbase that never sits down.</p>

          <div className="flex items-center gap-3 mt-6">
            {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full bg-[var(--pz-ink-2)] hover:bg-[var(--pz-red)] transition"><Facebook className="h-4 w-4" /></a>}
            {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full bg-[var(--pz-ink-2)] hover:bg-[var(--pz-red)] transition"><Instagram className="h-4 w-4" /></a>}
            {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-full bg-[var(--pz-ink-2)] hover:bg-[var(--pz-red)] transition"><Youtube className="h-4 w-4" /></a>}
          </div>
        </div>

        <div>
          <div className="font-display uppercase tracking-[0.15em] text-sm text-white mb-4">Visit Us</div>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[var(--pz-red)]" /> {settings.address}</li>
            <li className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 text-[var(--pz-red)]" /> {settings.contact_phone}</li>
            <li className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 text-[var(--pz-red)]" /> {settings.contact_email}</li>
          </ul>
        </div>

        <div>
          <div className="font-display uppercase tracking-[0.15em] text-sm text-white mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/events" className="hover:text-white">Events</Link></li>
            <li><Link to="/results" className="hover:text-white">Match Results</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/shop" className="hover:text-white">Shop</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/admin/login" className="hover:text-white opacity-70">Admin</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[var(--pz-line)] py-5 text-center text-xs uppercase tracking-[0.2em] text-zinc-500">
        © {new Date().getFullYear()} {settings.site_name}. All rights reserved.
      </div>
    </footer>
  );
}
